import React, { useState } from 'react';
import { FiLogOut, FiUser, FiAward, FiSun, FiMoon, FiMenu, FiUserPlus } from 'react-icons/fi';
import { IoShieldCheckmarkSharp } from 'react-icons/io5';
import { User } from '../../types';
import { useModalStore } from '../../stores';
import { useTheme } from '../../hooks/useTheme';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';
import AdminApprovalModal from '../Admin/AdminApprovalModal';
import MasterManagementModal from '../Admin/MasterManagementModal';
import NotificationDropdown from '../Community/NotificationDropdown';

interface HeaderProps {
  user: User;
  onLogout: () => void;
  onToggleSidebar?: () => void;
  showSidebarToggle?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onToggleSidebar,
  showSidebarToggle = false,
}) => {
  const { openModal } = useModalStore();
  const { theme, toggleTheme } = useTheme();
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  return (
    <>
      <header className={`${showSidebarToggle ? 'app-navbar' : ''} app-topbar sticky top-0 z-40 border-b`}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {showSidebarToggle && (
                <button
                  onClick={onToggleSidebar}
                  className="inline-flex lg:hidden items-center justify-center w-10 h-10 rounded-lg bg-surface hover:bg-surface-elevated text-foreground hover:text-primary transition-all"
                  aria-label="Toggle navigation"
                >
                  <FiMenu className="h-5 w-5" />
                </button>
              )}

              <div className={`flex items-center ${showSidebarToggle ? 'lg:hidden' : ''}`}>
                <img src="/trade-pilot-logo.png" alt="Trade Pilot" className="h-9 w-9 lg:h-[60px] lg:w-[60px] object-cover" />
                <div>
                  <h1 className="lg:text-2xl text-lg title-brand-gradient">
                    Trade Pilot
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {user.role === 'admin' && (
                <button
                  onClick={() => setShowMasterModal(true)}
                  className="hidden sm:flex items-center gap-2 bg-surface-elevated border border-border px-4 py-2 rounded-lg text-sm font-semibold hover:border-primary/40 hover:text-primary transition-all"
                >
                  <FiUserPlus className="h-4 w-4" />
                  <span>Add Master</span>
                </button>
              )}

              <NotificationDropdown />

              <button
                onClick={toggleTheme}
                className="relative w-10 h-10 rounded-lg bg-surface hover:bg-surface-elevated transition-all flex items-center justify-center text-foreground hover:text-primary"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <FiSun className="h-5 w-5 text-warning" />
                ) : (
                  <FiMoon className="h-5 w-5" />
                )}
              </button>

              <div className="hidden sm:flex items-center gap-2.5 px-3 py-2 bg-surface-elevated rounded-lg border border-border shadow-sm hover:shadow-md transition-all duration-200">
                {user.profileImage ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-border shadow-sm bg-surface">
                    <img
                      src={getProfileImageUrl(user.profileImage)}
                      alt={user.name}
                      crossOrigin="anonymous"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex items-center justify-center rounded-full ring-2 ring-white/20 ${user.role === 'master'
                      ? 'w-9 h-9 bg-gradient-to-br from-accent-500 to-accent-600'
                      : user.role === 'admin'
                        ? 'w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm'
                        : 'w-9 h-9 bg-gradient-to-br from-gray-400 to-gray-500'
                      }`}
                  >
                    {user.role === 'master' ? (
                      <FiAward className="h-4 w-4 text-white" />
                    ) : user.role === 'admin' ? (
                      <IoShieldCheckmarkSharp className="h-5 w-5 text-white" />
                    ) : (
                      <FiUser className="h-4 w-4 text-white" />
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-foreground leading-none">{user.name}</p>
                  <p className="text-xs text-muted capitalize mt-0.5">{user.role}</p>
                </div>
              </div>

              <button
                onClick={() => setShowLogoutModal(true)}
                className="btn-ghost flex items-center gap-2 text-sm px-3 py-2"
                title="Logout"
              >
                <FiLogOut className="h-4 w-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <AdminApprovalModal />
      {user.role === 'admin' && (
        <MasterManagementModal
          isOpen={showMasterModal}
          onClose={() => setShowMasterModal(false)}
        />
      )}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Confirm Logout</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to logout?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-danger hover:opacity-90 text-white rounded-lg font-semibold transition-colors"
              >
                Confirm Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
